import { ReactElement, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Component as RightArrowIcon } from 'icons/icon-ui--arrow-next.svg';
import { Component as LeftArrowIcon } from 'icons/icon-ui--arrow-prev.svg';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { clearProductCache } from '../../services/product/product.queries';
import { SkuCarouselImage } from '../../models/product.types';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { Favorite } from '../favorite/Favorite';
import styles from './ProductDisplayPage.module.scss';

interface Props {
  images: SkuCarouselImage[];
  skuId: number;
  isFavorite?: boolean;
}

export function ImageCarousel({
  images,
  skuId,
  isFavorite = false,
}: Props): ReactElement {
  const [currentImage, setCurrentImage] = useState(0);
  const [direction, setDirection] = useState<'left' | 'right'>('right');

  const { isActive } = useUser();
  const { isLoggedIn } = useAuth();
  const queryClient = useQueryClient();
  const { slug } = useParams();

  const prevSlide = () => {
    if (!images?.length) return;
    setDirection('left');
    setCurrentImage((prevIndex) => (prevIndex === 0 ? images.length - 1 : prevIndex - 1));
  };

  const nextSlide = () => {
    if (!images?.length) return;
    setDirection('right');
    setCurrentImage((prevIndex) => (prevIndex === images.length - 1 ? 0 : prevIndex + 1));
  };

  const onFavoriteAction = () => {
    clearProductCache(queryClient, slug || '');
  };

  const total = images.length;

  return (
    <div className={styles.image_carousel_container}>
      <div className={styles.carousel_main_slide_container}>
        {isLoggedIn && isActive && (
          <Favorite isFavorite={isFavorite} skuId={skuId} onClick={onFavoriteAction} />
        )}
        <TransitionGroup component={null}>
          <CSSTransition
            key={currentImage}
            timeout={300}
            classNames={`slide-${direction}`}
          >
            <div
              className={styles.carousel_main_slide}
            >
              <img
                src={images[currentImage].image.url}
                srcSet={images[currentImage].srcSet}
                alt={
                  images[currentImage].image_alt
                  || `Slide ${currentImage + 1}`
                }
              />
              <button
                onClick={prevSlide}
                type="button"
                aria-label="Previous image"
                className={`${styles.carousel_buttons} ${styles.prev_button}`}
              >
                <LeftArrowIcon aria-hidden="true" focusable="false" />
              </button>
              <button
                onClick={nextSlide}
                type="button"
                aria-label="Next image"
                className={`${styles.carousel_buttons} ${styles.next_button}`}
              >
                <RightArrowIcon aria-hidden="true" focusable="false" />
              </button>
            </div>
          </CSSTransition>
        </TransitionGroup>
        <div aria-live="polite" aria-atomic="true" className={styles.sr_only}>
          {`Image ${currentImage + 1} of ${total}`}
        </div>
      </div>

      <div className={styles.image_preview_container}>
        <hr />
        <div className={styles.carousel_slide_container}>
          {images?.map((image, index) => (
            <div
              key={image.image_alt}
              className={`${styles.slide} ${index === currentImage ? styles.selected : ''}`}
              aria-label={`Show image ${index + 1} of ${total}`}
              aria-current={index === currentImage ? true : undefined}
              tabIndex={0}
              role="button"
              onClick={() => setCurrentImage(index)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setCurrentImage(index);
                }
              }}
            >
              <img
                src={image.image.url}
                srcSet={image.srcSet}
                alt={image.image_alt || `Slide ${index + 1}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
